import { LeftOutlined } from "@ant-design/icons";
import {
  ErrorAlert,
  ReturnDetailsCustomerInfo,
  ReturnDetailsExternalNotes,
  ReturnDetailsInternalNotes,
  ReturnDetailsMainInfo,
  ReturnDetailsPackageInfo,
  ReturnDetailsShippingInfo,
  ReturnActionButtons,
  ReturnRestockConfirmationModal,
  ReturnRTSDecisionModal,
} from "@secondcloset/web-components";
import { Col, Row, Space, Spin, Typography } from "antd";
import { get } from "lodash-es";
import React, { FC, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useQuery } from "react-query";

import { Fulfillment, Warehouse } from "@secondcloset/types";

// Hooks
import { useOrganizationContext } from "../../../contextProviders/organization/OrganizationProvider";
import { useOrdersV2 } from "../../../hooks/api/fulfillment/orders";
import Organization from "../../../lib/organization";

// Helpers
import { ReturnStatus } from "../../_common/organization/OrganizationDetailsPage/ReturnsTable/helpers";
import { fetchReturnDetails, updateReturn } from "hooks/api/warehouse/returns";
import { useMutation } from "react-query-legacy";

type Order = Fulfillment.Order;
type Return = Warehouse.Return;

/* eslint-disable react-hooks/exhaustive-deps */
const ReturnDetailsPage: FC = () => {
  const { data: organizationDetails } = useOrganizationContext();
  const params = useParams();
  const returnId = get(params, "return_id", "");
  const history = useHistory();
  const [orders, setOrders] = useState<Order[]>();
  const [newOrderNumber, setNewOrderNumber] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>();
  const [orderNumberModal, setOrderNumberModalVisible] = useState(false);
  const [confirmRestockModalVisible, setConfirmRestockModalVisible] = useState(false);
  const [error, setError] = useState("");

  const isVessi = Organization.isVessi(organizationDetails);

  const {
    ordersV2,
    loading: orderLoading,
    error: orderError,
    fetchOrdersV2,
    searchLoading,
    searchOrderV2,
    fetchAllOrdersV2,
    allOrdersLoading,
    fetchAllOrdersSuccess,
    setFetchAllOrdersSuccess,
  } = useOrdersV2();

  const returnDetails = useQuery(["fetchReturnById"], () => fetchReturnDetails(returnId), {
    onSuccess: () => {
      setError("");
    },
    onError: setError,
  });

  const [doUpdateReturn] = useMutation(updateReturn, {
    onSuccess: () => {
      setError("");
      returnDetails.refetch({ throwOnError: true });
    },
    onError: setError,
  });

  useEffect(() => {
    if (organizationDetails?.id && returnDetails?.data?.external_order_number) {
      fetchOrdersV2({
        organization_id: organizationDetails?.id,
        external_order_number: returnDetails?.data?.external_order_number,
      });
    }
  }, [organizationDetails?.id, returnDetails?.data?.external_order_number]);

  const fetchAllOrders = async () => {
    const foundOrders = await fetchAllOrdersV2({
      organization_id: organizationDetails?.id,
      external_order_number: newOrderNumber,
    });
    setOrders(foundOrders);
  };

  const handleNewOrderClick = async (externalOrderNumber: string) => {
    const newOrder = await searchOrderV2({
      organization_id: organizationDetails?.id,
      external_order_number: externalOrderNumber,
    });
    newOrder?.id && window.open(`/fulfillment/orders/${newOrder.id}`);
  };

  const handleCancelOrder = () => {
    doUpdateReturn({
      id: returnId,
      status: ReturnStatus.CANCELLED,
    });
  };

  const handleRestock = () => {
    setConfirmRestockModalVisible(false);
    doUpdateReturn({
      id: returnId,
      status: ReturnStatus.COMPLETED,
    });
  };

  const handleResetModal = () => {
    setOrderNumberModalVisible(false);
    setNewOrderNumber("");
    setOrders([]);
    setSelectedOrder(null);
    setFetchAllOrdersSuccess(false);
  };

  const handleReship = async () => {
    handleResetModal();
    doUpdateReturn({
      id: returnId,
      status: ReturnStatus.TO_BE_RESHIPPED,
      reship_external_order_number: newOrderNumber,
    });
  };

  const handleActionRTS = (key: string) => {
    switch (key) {
      case "restock":
        setConfirmRestockModalVisible(true);
        break;
      case "reship":
        setOrderNumberModalVisible(true);
        break;
      default:
        return;
    }
  };

  return (
    <div className="transparent-page-container" id="return-details-page">
      <Spin spinning={returnDetails?.isLoading}>
        <div className="section-container">
          <Typography.Link
            style={{ fontSize: 16, lineHeight: 3 }}
            onClick={() => history.push(`/organizations/${organizationDetails?.id}/returns`)}
          >
            <LeftOutlined style={{ marginRight: 4 }} />
            {organizationDetails?.name} Returns
          </Typography.Link>
          <Row justify="space-between">
            <Typography.Title style={{ margin: 0 }} level={3}>
              RMA: {returnDetails?.data?.rma}
            </Typography.Title>
            <ReturnActionButtons
              isVessi={isVessi}
              isReturnUpdating={returnDetails?.isLoading}
              handleActionRTS={handleActionRTS}
              handleCancelOrder={handleCancelOrder}
              returnDetails={returnDetails?.data}
            />
          </Row>
        </div>
        <ErrorAlert error={error || orderError} />
        <div className="section-container">
          <ReturnDetailsMainInfo
            isSearching={searchLoading}
            orderLoading={orderLoading}
            returnLoading={returnDetails?.isLoading}
            fetchNewOrder={handleNewOrderClick}
            returnDetails={returnDetails?.data}
            orderDate={ordersV2?.created_at}
          />
        </div>
        <Row justify="space-between">
          <Col flex={2} style={{ marginRight: 20 }}>
            <div
              className="section-container"
              style={{
                padding: 24,
              }}
            >
              <ReturnDetailsPackageInfo returnDetails={returnDetails?.data} />
            </div>
            <div className="section-container">
              <ReturnDetailsExternalNotes
                disableEdit
                isReturnUpdating={returnDetails?.isLoading}
                returnDetails={returnDetails?.data}
                doUpdateReturn={updateReturn}
              />
            </div>
          </Col>
          <Col>
            <Space direction="vertical">
              <ReturnDetailsCustomerInfo
                orderLoading={orderLoading}
                returnLoading={returnDetails?.isLoading}
                orderDetails={ordersV2}
                returnDetails={returnDetails?.data}
              />
              <div className="section-container" style={{ padding: 24 }}>
                <ReturnDetailsShippingInfo returnDetails={returnDetails?.data} />
              </div>
              <div className="section-container" style={{ padding: 24 }}>
                <ReturnDetailsInternalNotes returnDetails={returnDetails?.data} />
              </div>
            </Space>
          </Col>
        </Row>
        <ReturnRTSDecisionModal
          orderNumberModal={orderNumberModal}
          handleReship={handleReship}
          isUpdatingReturn={returnDetails?.isLoading}
          fetchOrders={fetchAllOrders}
          isFetchingOrders={allOrdersLoading}
          isFetchingOrdersSuccess={fetchAllOrdersSuccess}
          orders={orders}
          error={orderError}
          newOrderNumber={newOrderNumber}
          setNewOrderNumber={setNewOrderNumber}
          setSelectedOrder={(val?: Order | null) => setSelectedOrder(val || null)}
          selectedOrder={selectedOrder || undefined}
          handleResetModal={handleResetModal}
        />
        <ReturnRestockConfirmationModal
          handleRestock={handleRestock}
          confirmRestockModalVisible={confirmRestockModalVisible}
          setConfirmRestockModalVisible={setConfirmRestockModalVisible}
          isReturnUpdating={returnDetails?.isLoading}
        />
      </Spin>
    </div>
  );
};

export default ReturnDetailsPage;
